/**
 * [M4-05] Mail send-path request contracts — ไฟล์ใหม่แยกจาก mail.ts (เจ้าของ M4-02 · hotspot §8)
 * เหมือนแบบ mail-contacts.ts ของ M4-11: งานอื่นเพิ่มไฟล์ใหม่ + บรรทัด export ของตัวเองใน index.ts
 * Response ใช้ MailDraft/MailSendResult ที่ scaffold นิยามไว้ใน mail.ts แล้ว
 */
import { z } from "zod";
import { MailDraft } from "./mail.ts";

/** ที่อยู่ผู้รับใน request (name optional — ไม่ส่ง = null) */
export const MailAddressInput = z.object({
  address: z.email(),
  name: z.string().max(200).nullable().optional(),
});
export type MailAddressInput = z.infer<typeof MailAddressInput>;

export const MailReplyContext = z.object({
  message_id: z.uuid(),
  type: z.enum(["reply", "reply_all", "forward"]),
});
export type MailReplyContext = z.infer<typeof MailReplyContext>;

const draftCommon = {
  thread_id: z.uuid().nullable().optional(),
  from_address: z.email(),
  to: z.array(MailAddressInput).max(100).default([]),
  cc: z.array(MailAddressInput).max(100).default([]),
  bcc: z.array(MailAddressInput).max(100).default([]),
  subject: z.string().max(998).nullable().optional(),
  body_html: z.string().max(5 * 1024 * 1024).nullable().optional(),
  body_text: z.string().max(5 * 1024 * 1024).nullable().optional(),
  attachment_ids: z.array(z.uuid()).max(50).default([]),
  reply_context: MailReplyContext.nullable().optional(),
} as const;

export const MailDraftCreate = z.object(draftCommon);
export type MailDraftCreate = z.infer<typeof MailDraftCreate>;

/**
 * autosave = PATCH ทุก field optional (ยกเว้น from_address ต้องผ่าน identity check เมื่อส่งมา)
 * [M4-02G] partial-PATCH semantics: ห้าม reuse `.default()` ของ draftCommon — default ทำให้ field ที่
 * ไม่ได้ส่ง (to/cc/bcc/attachment_ids) กลายเป็น "ส่งมาเป็น []" แล้ว wipe ค่าเดิม · default ใช้เฉพาะตอน
 * create · explicit null ล้างค่าได้เฉพาะ field ที่ contract อนุญาต (subject/body_html/body_text/reply_context/thread_id)
 */
const draftPatchFields = {
  thread_id: z.uuid().nullable().optional(),
  from_address: z.email().optional(),
  to: z.array(MailAddressInput).max(100).optional(),
  cc: z.array(MailAddressInput).max(100).optional(),
  bcc: z.array(MailAddressInput).max(100).optional(),
  subject: z.string().max(998).nullable().optional(),
  body_html: z.string().max(5 * 1024 * 1024).nullable().optional(),
  body_text: z.string().max(5 * 1024 * 1024).nullable().optional(),
  attachment_ids: z.array(z.uuid()).max(50).optional(),
  reply_context: MailReplyContext.nullable().optional(),
} as const;

export const MailDraftPatch = z.object(draftPatchFields).refine(
  (v) => Object.keys(v).length > 0,
  { message: "ต้องส่ง field อย่างน้อยหนึ่ง field" },
);
export type MailDraftPatch = z.infer<typeof MailDraftPatch>;

export const MailDraftList = z.object({
  items: z.array(MailDraft),
});
export type MailDraftList = z.infer<typeof MailDraftList>;

/**
 * POST /api/v1/mail/drafts/:id/send —
 * undo_seconds ≤ MAIL_UNDO_SEND_MAX_SEC (30) · send_at = กำหนดส่ง (≤ 30 วัน — ตรวจซ้ำใน outbox)
 * ส่งทั้งสองชิ้นพร้อมกันไม่ได้ (undo ใช้ sendAfter เหมือน schedule)
 */
export const MailDraftSend = z
  .object({
    send_at: z.iso.datetime().optional(),
    undo_seconds: z.number().int().min(0).max(30).optional(),
  })
  .refine((v) => !(v.send_at !== undefined && v.undo_seconds !== undefined), { message: "เลือกอย่างใดอย่างหนึ่ง: send_at หรือ undo_seconds" });
export type MailDraftSend = z.infer<typeof MailDraftSend>;
